import generatePDF, { Margin, Options, Resolution } from 'react-to-pdf';
import { Button, CircularProgress, Paper, Typography } from '@mui/material';
import { ConnectionRequestAcPdf } from '../../../../components/pdf/connection-request-ac.pdf';
import { useState } from 'react';
import useRecaptcha from '../../../../hooks/google-recaptcha.hook';
import { useSnackbars } from '../../../../hooks/snackbar.hook';
import { useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { sendConnectionACRequest } from '../../../../services/requests.service';
import { AxiosError } from 'axios';
import { showAxiosMessageError } from '../../../../services/error-message.service';
import { ConnectionRequestFormDto } from '../../../../services/dtos/form/connection-request-form.dto';

const options: Options = {
    filename: 'demande_raccordement_ac.pdf',
    // default is `save`
    method: 'open',
    // default is Resolution.MEDIUM = 3, which should be enough, higher values
    // increases the image quality but also the size of the PDF, so be careful
    // using values higher than 10 when having multiple pages generated, it
    // might cause the page to crash or hang.
    resolution: Resolution.NORMAL,
    page: {
        // margin is in MM, default is Margin.NONE = 0
        margin: Margin.SMALL,
    },
    canvas: {
        // default is 'image/jpeg' for better size performance
        mimeType: 'image/png',
        qualityRatio: 1,
    },
    // Customize any value passed to the jsPDF instance and html2canvas
    // function. You probably will not need this and things can break,
    // so use with caution.
    overrides: {
        // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
        pdf: {
            compress: true,
        },
        // see https://html2canvas.hertzen.com/configuration for more options
        canvas: {
            useCORS: true,
        },
    },
};

export default function ConnectionRequestAcPage() {
    const [validate, setValidate] = useState(false);

    //Recaptcha
    const { getRecaptchaToken } = useRecaptcha('raccordementac');

    const showMessage = useSnackbars();

    const navigate = useNavigate();

    // you can use a function to return the target element besides using React refs
    const openPDF = (values: ConnectionRequestFormDto) => {
        setValidate(true);
        generatePDF(() => document.getElementById('wrapper'), options)
            .then(response => {
                getRecaptchaToken()
                    .then(token => {
                        //On met à jour le recaptcha
                        sendConnectionACRequest(response.output('blob'), values, token)
                            .then(() => {
                                showMessage.successAlert('Votre demande de contact a bien été prise en compte.');
                                navigate('/');
                            })
                            .catch((error: Error | AxiosError) => {
                                console.log(error);
                                showAxiosMessageError(error, showMessage);
                            })
                            .finally(() => {
                                setValidate(false);
                            });
                    })
                    .catch((error: Error | AxiosError) => {
                        console.log(error);
                        showAxiosMessageError(error, showMessage);
                    });
            })
            .finally(() => {
                setValidate(false);
            });
    };

    //Mise en place des valeurs par defauts
    const defaultValues: ConnectionRequestFormDto = {
        objet: '',
        objetAutre: '',
        estUnParticulierDemandeur: '',
        civiliteDemandeur: '',
        nomDemandeur: '',
        prenomDemandeur: '',
        nomEntrepriseDemandeur: '',
        dossierSuiviParDemandeur: '',
        siretDemandeur: '',
        adresseDemandeur: '',
        codePostalDemandeur: '',
        communeDemandeur: '',
        telephoneDemandeur: '',
        emailDemandeur: '',
        estUnParticulierProprietaire: '',
        civiliteProprietaire: '',
        nomProprietaire: '',
        prenomProprietaire: '',
        nomEntrepriseProprietaire: '',
        dossierSuiviParProprietaire: '',
        siretProprietaire: '',
        adresseProprietaire: '',
        codePostalProprietaire: '',
        communeProprietaire: '',
        telephoneProprietaire: '',
        emailProprietaire: '',
        estUnParticulierPayeur: '',
        civilitePayeur: '',
        nomPayeur: '',
        prenomPayeur: '',
        nomEntreprisePayeur: '',
        dossierSuiviParPayeur: '',
        siretPayeur: '',
        adressePayeur: '',
        codePostalPayeur: '',
        communePayeur: '',
        telephonePayeur: '',
        emailPayeur: '',
        typeLogement: '',
        batiment: '',
        numEtage: '',
        pallier: '',
        partiesCommunes: '',
        commerce: '',
        codeNAFCommerce: '',
        industrielle: '',
        codeNAFIndustrielle: '',
        nbPieceEau: '',
        addressControle: '',
        codePostalControle: '',
        communeControle: '',
        referencesCadastralesSection: '',
        referencesCadastralesParcelle: '',
        referencesCadastralesLot: '',
        personnePresente: '',
        personnePresenteAutre: '',
        civiliteControle: '',
        nomControle: '',
        prenomControle: '',
        telephoneControle: '',
        rapport: '',
        rapportTransmission: '',
        rapportEmail: '',
    };

    return (
        <Paper>
            {/*Formik pour envoyer les données en plus*/}
            <Formik
                initialValues={defaultValues}
                onSubmit={(values: ConnectionRequestFormDto) => {
                    openPDF(values);
                }}
            >
                {/*form*/}
                <Form>
                    {/*pdf avec les champs formulaire*/}
                    <ConnectionRequestAcPdf />
                    <Typography
                        component={'div'}
                        style={{
                            width: '100%',
                            minWidth: 'fit-content',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {/*bouton d'action*/}
                        <Button
                            type={'submit'}
                            variant={'contained'}
                            style={{ marginTop: '20px', marginBottom: '20px', position: 'relative' }}
                            disabled={validate}
                        >
                            Envoyer le document
                            {validate ? (
                                <CircularProgress
                                    style={{
                                        marginLeft: '20px',
                                        position: 'absolute',
                                        right: '-64px',
                                    }}
                                />
                            ) : null}
                        </Button>
                    </Typography>
                </Form>
            </Formik>
        </Paper>
    );
}
